import { Button, Text, TextField, Toggle } from '@cinesend/ui-kit'
import useStore from '../../hooks/use_store'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import useAuth from '../../hooks/use_auth'
import { modelTypes } from '../../constants/model_types'
import { DISABLED_REASONS } from '../../constants/disabled_reasons'
import SettingsCard from '../settings_card'
import DisabledTooltip from '../disabled_tooltip'

const Details = ({ collection, schemaLocked }) => {
  const { updateModel, deleteModel } = useStore()
  const navigate = useNavigate()
  const { airlineID, cycleID } = useParams()
  const { canEditModel: canEditCollection } = useAuth(modelTypes.Collection)
  const [name, setName] = useState(collection.name)
  const [outputKey, setOutputKey] = useState(collection.output_key)

  const permissionReason = !canEditCollection ? DISABLED_REASONS.noPermission : null
  const localizedReason = !canEditCollection
    ? DISABLED_REASONS.noPermission
    : schemaLocked
      ? DISABLED_REASONS.schemaLocked
      : null

  const update = (data) => {
    updateModel.mutate({
      endpoint: `collections`,
      id: collection.id,
      data,
    })
  }

  const deleteList = () => {
    deleteModel.mutate(
      {
        endpoint: 'collections',
        id: collection.id,
        deleteNoun: 'list',
        deleteName: collection.name,
      },
      {
        onSuccess: () => {
          if (cycleID) {
            navigate(`/airlines/${airlineID}/cycles/${cycleID}/lists`)
          } else if (collection.schema_id) {
            navigate(`/settings/schemas/${collection.schema_id}/lists`)
          } else {
            navigate(-1)
          }
        },
      }
    )
  }

  return (
    <div className='flex w-full flex-col gap-12'>
      <SettingsCard title='Details'>
        <div className='grid gap-4 sm:grid-cols-2'>
          <DisabledTooltip reason={permissionReason} className='block w-full'>
            <TextField
              label='Name'
              value={name ?? ''}
              onChange={(e) => setName(e.target.value)}
              onBlur={(e) => update({ name: e.target.value })}
              disabled={!canEditCollection}
            />
          </DisabledTooltip>
          <DisabledTooltip reason={permissionReason} className='block w-full'>
            <TextField
              label='Slug'
              value={outputKey ?? ''}
              onChange={(e) => setOutputKey(e.target.value)}
              onBlur={(e) => update({ output_key: e.target.value })}
              disabled={!canEditCollection}
            />
          </DisabledTooltip>
        </div>
        <div className='flex flex-col divide-y divide-[color:var(--cs-color-border-subtle)]'>
          <div className='flex items-center justify-between gap-6 py-3 first:pt-0 last:pb-0'>
            <Text as='p' variant='sm-medium'>
              Localized labels
            </Text>
            <DisabledTooltip reason={localizedReason}>
              <Toggle
                label='Localized labels'
                pressed={collection.is_localizable}
                onToggle={() => update({ is_localizable: !collection.is_localizable })}
                disabled={!canEditCollection || schemaLocked}
              />
            </DisabledTooltip>
          </div>
          <div className='flex items-center justify-between gap-6 py-3 first:pt-0 last:pb-0'>
            <Text as='p' variant='sm-medium'>
              Admin only
            </Text>
            <DisabledTooltip reason={permissionReason}>
              <Toggle
                label='Admin only'
                pressed={collection.admin_only}
                onToggle={() => update({ admin_only: !collection.admin_only })}
                disabled={!canEditCollection}
              />
            </DisabledTooltip>
          </div>
        </div>
      </SettingsCard>
      {canEditCollection ? (
        <SettingsCard title='Danger zone' description='Permanent actions that cannot be undone.'>
          <div className='flex items-center justify-between gap-6'>
            <div className='min-w-0 flex-1'>
              <Text variant='sm-medium'>Delete this list</Text>
              <Text as='p' variant='sm' tone='secondary' className='mt-1'>
                Permanently delete this list and its items. This can’t be undone.
              </Text>
            </div>
            <Button variant='destructive' onClick={deleteList}>
              Delete
            </Button>
          </div>
        </SettingsCard>
      ) : null}
    </div>
  )
}

export default Details
